// @flow

import React, {useRef, useEffect, useLayoutEffect, useState, useContext, useCallback} from 'react';
import mergeRefs from 'merge-refs';
import {useCallbackWithRef} from '@pb/kit-util-use-callback-with-ref';
import {withCssModule} from '@pb/kit-util-with-css-module';

import styles from './data-table-scroll.module.styl';
import {DataTableDragEventsContext} from './contexts/data-table-drag-events-context';
import {
    getCellEndIndex,
    getCellStartIndex,
    getLeftOffset,
    getRightOffset,
    getVisibleCellData,
    shouldUpdateOnScroll
} from './utils';
import {
    HORIZONTAL_SCROLL_DELTA,
    TABLE_PARTS,
    TIMER_DELAY,
    virtualizedCellsArea
} from './constants';
import {ScrollContext} from './contexts/data-table-scroll-context';
import {DataTableScrollArrows} from './data-table-scroll-arrows';

import type {Element as ReactElement, Node} from 'react';

import type {Column, RenderTableType, RenderHeaderType, RenderCustomArrows} from './typedef';
import type {DataTableDragEventsContextType} from './contexts/data-table-drag-events-context';
import type {ScrollContextType} from './contexts/data-table-scroll-context';


const classNames = withCssModule(styles);

type Props<DataColumn> = {|
    columnsData: {
        columns: Array<Column<DataColumn>>,
        width: number,
        widthArray: Array<number>,
        offsetWidth: Array<number>,
    },
    isVirtualizedCells: boolean,
    overscanCellsRatio?: number,
    renderHeaders: (
        RenderHeaderType<DataColumn>,
        position: $Values<typeof TABLE_PARTS>,
        stickyScroll: boolean
    ) => ReactElement<*>,
    getTable: (RenderTableType<DataColumn>, position: $Values<typeof TABLE_PARTS>) => ReactElement<*> | null,
    renderCustomArrows?: RenderCustomArrows,
    disabledScroll?: boolean,
    onScrollableSizeChange?: (containerSize: number) => void,
    setScrollablePositionRatio?: (callback: (scrollRatio: number) => any) => void,
    wrapperElement?: HTMLElement | null,
|};

export const VirtualizedCellsArea = <DataColumn>(props: Props<DataColumn>): Node => {
    const {
        setScrollablePositionRatio,
        onScrollableSizeChange,
        isVirtualizedCells,
        columnsData,
        overscanCellsRatio,
        renderCustomArrows,
        disabledScroll
    } = props;

    const dataTableRef = useRef<HTMLElement | null>(null);

    const {onDrop, onDragOver, onDragEnter} = useContext<DataTableDragEventsContextType>(DataTableDragEventsContext);
    const {
        scrollContainerRef,
        getScrollInstance,
        addListener
    } = useContext<ScrollContextType>(ScrollContext);

    const lastScrollLeftOffset = useRef(0);

    const prepareCellDataState = useCallback(offset => getVisibleCellData({
        offset,
        offsetWidth: columnsData.offsetWidth,
        overscanCellsRatio: Number(overscanCellsRatio),
        viewportWidth: window.innerWidth
    }), [columnsData.offsetWidth, overscanCellsRatio]);

    const [state, setState] = useState(prepareCellDataState(lastScrollLeftOffset.current));

    const setScrollablePosition = useCallbackWithRef(async (scrollRatio: number) => {
        if (!getScrollInstance()) {
            await new Promise(resolve => {
                addListener('initialized', resolve);
            });
        }

        // $FlowFixMe TODO: k.garnaga@spd-ukraine.com - waiting for migration to TS
        const scrollWidth = getScrollInstance()?.elements().viewport?.scrollWidth ?? 0;
        // $FlowFixMe TODO: k.garnaga@spd-ukraine.com - waiting for migration to TS
        getScrollInstance()?.elements().viewport?.scrollTo({left: scrollWidth * scrollRatio});
    });

    useEffect(() => {
        if (setScrollablePositionRatio) {
            setScrollablePositionRatio(setScrollablePosition);
        }
    }, [setScrollablePositionRatio, setScrollablePosition]);

    const getVisibleCellRange = useCallbackWithRef((scrollOffsetLeft: number, _isVirtualizedCells: boolean) => {
        const {cellEndIndex, cellStartIndex} = state;
        const offsetWidth = props.columnsData.offsetWidth;
        const offset = scrollOffsetLeft;
        lastScrollLeftOffset.current = offset;

        if (_isVirtualizedCells) {
            const viewportWidth = window.innerWidth;
            if (shouldUpdateOnScroll({offsetWidth, cellEndIndex, cellStartIndex, viewportWidth, offset})) {
                const newCellStartIndex = getCellStartIndex({offset, offsetWidth, viewportWidth});
                const newCellEndIndex = getCellEndIndex({offset, viewportWidth, overscanCellsRatio, offsetWidth});
                const rightOffset = getRightOffset(offsetWidth, newCellEndIndex);
                const leftOffset = getLeftOffset(offsetWidth, newCellStartIndex);

                const cellStartIndexIsChanged = newCellStartIndex !== cellStartIndex;
                const cellEndIndexIsChanged = newCellEndIndex !== state.cellEndIndex;

                if ((cellStartIndexIsChanged && !cellEndIndexIsChanged) || cellEndIndexIsChanged) {
                    setState({
                        cellStartIndex: newCellStartIndex,
                        cellEndIndex: newCellEndIndex,
                        leftOffset,
                        rightOffset
                    });
                }
            }
        }
    });

    useLayoutEffect(() => {
        if (isVirtualizedCells) {
            setState(prepareCellDataState(lastScrollLeftOffset.current));
            getVisibleCellRange(lastScrollLeftOffset.current, isVirtualizedCells);
        } else {
            setState(prev => {
                if (prev.cellStartIndex === 0) {
                    return prev;
                }
                return {
                    ...prev,
                    cellStartIndex: 0
                };
            });
        }
    }, [isVirtualizedCells, getVisibleCellRange, prepareCellDataState]);

    const timer = useRef();

    const observedResize = useCallbackWithRef(() => {
        clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            getVisibleCellRange(lastScrollLeftOffset.current, isVirtualizedCells);
        }, TIMER_DELAY);
    });

    useEffect(() => {
        if (dataTableRef.current) {
            const resizeObserver = new ResizeObserver(() => {

                if (dataTableRef.current && onScrollableSizeChange) {
                    onScrollableSizeChange(dataTableRef.current.offsetWidth);
                }

                if (isVirtualizedCells) {
                    observedResize();
                }
            });

            resizeObserver.observe(dataTableRef.current);

            return () => {
                resizeObserver.disconnect();
            };
        }
    }, [isVirtualizedCells, onScrollableSizeChange, observedResize]);


    const updateScrollableData = useCallbackWithRef(() => {
        // $FlowFixMe TODO: k.garnaga@spd-ukraine.com - waiting for migration to TS
        const scrollLeft = getScrollInstance()?.elements().viewport?.scrollLeft ?? 0;
        if (Math.abs(lastScrollLeftOffset.current - scrollLeft) > HORIZONTAL_SCROLL_DELTA) {
            lastScrollLeftOffset.current = scrollLeft;
            getVisibleCellRange(scrollLeft, props.isVirtualizedCells);
        }
    });

    useEffect(() => {
        if (!isVirtualizedCells) {
            return;
        }

        // $FlowFixMe TODO: k.garnaga@spd-ukraine.com - waiting for migration to TS
        lastScrollLeftOffset.current = getScrollInstance()?.elements().viewport.scrollLeft || 0;
        addListener('scroll', updateScrollableData);
    }, [isVirtualizedCells, addListener, updateScrollableData, getScrollInstance]);

    const {cellEndIndex, cellStartIndex, leftOffset, rightOffset} = state;

    const renderData = {
        columns: columnsData.columns,
        width: columnsData.width,
        virtualizeHorizontalData: {
            cellStartIndex, cellEndIndex, rightOffset, leftOffset, isVirtualizedCells, renderCellPlaceholder: true
        }
    };

    const classes = classNames({
        'data-table__data_no-scroll': disabledScroll
    });

    const tableRows = props.getTable(renderData, TABLE_PARTS.right);

    return (
        <>
            {props.renderHeaders(renderData, TABLE_PARTS.right, true)}
            <div
                ref={mergeRefs(scrollContainerRef, dataTableRef)}
                className={classes}>
                <div className={virtualizedCellsArea} {...{onDrop, onDragOver, onDragEnter}}>
                    {tableRows}
                </div>
                <DataTableScrollArrows
                    renderCustomArrows={renderCustomArrows}
                />
            </div>
        </>
    );
};

            // @pb/generate-flow-typed Webpack Loader:
            // Duplicates all internal dependencies imports
            // to be sure all deps are parsed by this loader
            // cause Babel loader removes `import type ...`
            // and e.g. 'typedef.js' files are not included in dist
            
require("./typedef");

require("./constants");
